/// <reference types="@types/segment-analytics" />
import { Injectable, DOCUMENT, inject } from '@angular/core'
import { Operation } from '@apollo/client'
import { OperationDefinitionNode } from 'graphql/language/ast'
import { NavigationEnd, Router } from '@angular/router'
import { BehaviorSubject, filter, first, take } from 'rxjs'
import { tap } from 'rxjs/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import * as amplitude from '@amplitude/analytics-browser'
import { TabTrackingService } from '../tab-tracking/tab-tracking.service'
import {
  TabTrackingEvent,
  TabTrackingTransport,
} from '../tab-tracking/tab-tracking.types'
import {
  generateTabId,
  scrubSensitiveParams,
} from '../tab-tracking/tab-tracking.utils'

/** Declare analytics to the global window object */
declare global {
  interface Window {
    analytics: SegmentAnalytics.AnalyticsJS
  }
}

export enum AnalyticEventType {
  CREATION = 'created',
  DOWNLOAD = 'downloaded',
  UPDATE = 'updated',
  UPLOAD = 'uploaded',
  DELETE = 'deleted',
  LOGIN = 'identified',
  CLICK = 'clicked',
  HOVER = 'hovered',
}

interface AnalyticEventProperties {
  name: string
  url?: string
  variables?: Record<string, any>
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SegmentService {
  protected _document = inject<Document>(DOCUMENT)

  protected _router = inject(Router)

  protected _tabTracking = inject(TabTrackingService)

  protected _isEnabled: boolean

  private readonly forbiddenData = ['password']

  /** Segment tracking API, used with `sendBeacon` when the page is going away */
  private readonly segmentBeaconUrl = 'https://api.segment.io/v1/t'

  /** The Segment write key, kept for beacons */
  private writeKey: string | null = null

  /** The window object */
  private window: Window

  private amplitudeReplayInitialized = false

  private _isAnalyticsReady$ = new BehaviorSubject<boolean>(false)

  constructor() {
    const _document = this._document

    this.window = _document.defaultView as Window
  }

  public init(config: { segmentEnabled: boolean }): void {
    this._isEnabled = config.segmentEnabled
    this._tabTracking.init()
    this._router.events
      .pipe(
        filter(() => this._isEnabled),
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this),
        tap(() => this.page())
      )
      .subscribe()
    this._tabTracking.events$
      .pipe(
        filter(() => this._isEnabled),
        tap((event) => this.trackTabEvent(event)),
        untilDestroyed(this)
      )
      .subscribe()
  }

  /** Loads segment with the given api Key
   * For now it only loads the beta.wemaintain.com project,
   * In the future this will have to be handled with a injection token in
   * order to support multiples projects. But since we are limited on 2
   * projects on the segment plan, this is ok for now
   */
  public async load(apiKey: string, amplitudeApiKey?: string): Promise<void> {
    if (this._isEnabled) {
      this.writeKey = apiKey
      this.window.analytics.load(apiKey)
      if (amplitudeApiKey) {
        this.window.analytics.ready(async () => {
          await this.initAmplitudeSessionReplayBridge(amplitudeApiKey)
          this._isAnalyticsReady$.next(true)
        })
      }
    }
  }

  /** Use to identify the user we track
   * This would be use once the back end identifies the user as a persona i.e 'PM, asset, syndic' etc
   * Because as for now, it's make no sens to track the user by their id
   * @param { string } userId the id of the user
   * @param { Record<string, any> } traits the traits we use to identify him as human i.e 'firstname', 'email' etc etc
   */
  public identify(
    userId: string,
    traits: Record<string, any>,
    options?: SegmentAnalytics.SegmentOpts,
    shouldNotBeAnonymous?: boolean
  ): void {
    if (this._isEnabled && shouldNotBeAnonymous) {
      this._isAnalyticsReady$
        .pipe(
          filter(Boolean),
          take(1),
          tap(() => this.window.analytics.identify(userId, traits, options))
        )
        .subscribe()
    }
  }

  /** Use to track am event
   * @param { string } eventName the event name
   * @param { Record<string, any> } properties the props we use to qualify the event in GA, Intercom etc etc
   */
  public track(eventName: string, properties: Record<string, any>): void {
    this.addDefaultProperties?.(properties)
    if (this._isEnabled) {
      if (this.amplitudeReplayInitialized) {
        amplitude.track(eventName, properties)
      } else {
        this.window.analytics.track(eventName, properties)
      }
    }
  }

  /**
   * This method is dedicated to track graphql operation
   * @param {Operation} operation
   */
  public trackGraphqlRequest(operation: Operation): void {
    if (this.shouldBeTracked(operation)) {
      this.track(
        this.getEventNameFromOperationName(operation.operationName),
        this.getAnalyticEventProperties(operation)
      )
    }
  }

  /**
   * Sends a `page()` call carrying the tab properties, with auth tokens scrubbed from the URL
   */
  private page(): void {
    const properties = this.getPageProperties()
    this.window.analytics.page(properties, {
      context: {
        page: { url: properties['url'], search: properties['search'] },
      },
    })
  }

  /**
   * Sends a tab tracking event, with a beacon when the page is going away
   * @param {TabTrackingEvent} event
   */
  private trackTabEvent(event: TabTrackingEvent): void {
    if (
      event.transport === TabTrackingTransport.Beacon &&
      this.trackWithBeacon(event.name, { ...event.properties })
    ) {
      return
    }
    this.track(event.name, { ...event.properties })
  }

  /**
   * Sends a track call straight to the Segment HTTP API with `navigator.sendBeacon`,
   * which survives the page being unloaded (analytics.js queues would be lost).
   * @returns {boolean} whether the beacon was queued by the browser
   */
  private trackWithBeacon(
    eventName: string,
    properties: Record<string, unknown>
  ): boolean {
    const user = this.window.analytics?.user?.()
    const navigator = this.window.navigator
    if (!this.writeKey || !user || !navigator?.sendBeacon) {
      return false
    }
    this.addDefaultProperties(properties)
    const sessionId = this.amplitudeReplayInitialized
      ? amplitude.getSessionId()
      : undefined
    const payload = {
      writeKey: this.writeKey,
      type: 'track',
      event: eventName,
      properties,
      anonymousId: user.anonymousId(),
      userId: user.id() ?? undefined,
      messageId: `wm-beacon-${generateTabId(this.window.crypto)}`,
      timestamp: new Date().toISOString(),
      context: {
        page: {
          path: this.window.location.pathname,
          search: scrubSensitiveParams(this.window.location.search),
          url: scrubSensitiveParams(this.window.location.href),
          referrer: scrubSensitiveParams(this._document.referrer),
          title: this._document.title,
        },
        userAgent: navigator.userAgent,
        library: { name: 'wm-front-beacon', version: '1.0.0' },
      },
      ...(sessionId
        ? { integrations: { Amplitude: { session_id: sessionId } } }
        : {}),
    }
    try {
      return navigator.sendBeacon(
        this.segmentBeaconUrl,
        new Blob([JSON.stringify(payload)], { type: 'text/plain' })
      )
    } catch {
      return false
    }
  }

  private async initAmplitudeSessionReplayBridge(
    amplitudeApiKey: string
  ): Promise<void> {
    if (
      !this._isEnabled ||
      this.amplitudeReplayInitialized ||
      !this.window?.analytics
    ) {
      return
    }
    const deviceId =
      this.window.analytics.user?.()?.anonymousId?.() ?? undefined
    await amplitude.add(sessionReplayPlugin()).promise
    await amplitude.add(this.createSegmentDestinationPlugin()).promise
    await amplitude.init(amplitudeApiKey, {
      instanceName: 'session-replay',
      sessionTimeout: Number.MAX_SAFE_INTEGER,
      autocapture: false,
      deviceId,
    }).promise
    amplitude.remove('amplitude')
    this.amplitudeReplayInitialized = true
  }

  private createSegmentDestinationPlugin() {
    return {
      name: 'segment',
      type: 'destination',
      execute: async (event: any) => {
        const properties = event?.event_properties || {}
        this.window.analytics.track(event?.event_type, properties, {
          integrations: {
            Amplitude: {
              session_id: amplitude.getSessionId(),
            },
          },
        })
        return {
          code: 200,
          event,
          message: 'OK',
        }
      },
    } as const
  }

  /**
   * This method will specify if the method should be tracked
   * @param {Operation} operation
   * @returns {boolean}
   * @private
   */
  private shouldBeTracked(operation: Operation): boolean {
    return (
      (operation.query.definitions[0] as OperationDefinitionNode)?.operation ===
      'mutation'
    )
  }

  /**
   * This method will try to determine which kind of event the operation is
   * @param {string} operationName
   * @returns {string}
   * @private
   */
  private getEventNameFromOperationName(operationName: string): string {
    if (
      operationName.toLowerCase().includes('login') ||
      operationName.toLowerCase().includes('password')
    ) {
      return AnalyticEventType.LOGIN
    }
    if (
      operationName.toLowerCase().includes('add') ||
      operationName.toLowerCase().includes('create') ||
      operationName.toLowerCase().includes('import')
    ) {
      return AnalyticEventType.CREATION
    }
    if (operationName.toLowerCase().includes('delete')) {
      return AnalyticEventType.DELETE
    }
    if (
      operationName.toLowerCase().includes('download') ||
      operationName.toLowerCase().includes('generate') ||
      operationName.toLowerCase().includes('archive')
    ) {
      return AnalyticEventType.DOWNLOAD
    }
    if (operationName.toLowerCase().includes('upload')) {
      return AnalyticEventType.UPLOAD
    }
    return AnalyticEventType.UPDATE
  }

  /**
   * This method will return the properties of the request for the analytic event
   * @param {Operation} operation
   * @returns {AnalyticEventProperties}
   * @private
   */
  private getAnalyticEventProperties(
    operation: Operation
  ): AnalyticEventProperties {
    return {
      name: operation.operationName,
      variables: this.cleanVariables(operation.variables),
    }
  }

  /**
   * This method will remove any data we don't want to track
   * @param {Record<string, any>} variables
   * @returns {Record<string, any>}
   * @private
   */
  private cleanVariables(variables?: Record<string, any>): Record<string, any> {
    return variables
      ? Object.keys(variables)
          .filter((key) => !this.forbiddenData.includes(key))
          .reduce((previous, current) => {
            previous[current] = variables[current]
            return previous
          }, {})
      : {}
  }

  /**
   * This method is temporary, and aims to add default properties to the analytics events
   * These properties should be handled by segment itself but it doesn't seems to work for now
   * Auth tokens are scrubbed from the search, and the tab properties are added.
   * @param {Record<string, unknown>} properties
   * @private
   */
  private addDefaultProperties(properties?: Record<string, unknown>): void {
    if (!properties) {
      return
    }
    Object.assign(properties, this.getDefaultProperties())
  }

  private getDefaultProperties(): Record<string, unknown> {
    return {
      path: this.window.location.pathname,
      search: scrubSensitiveParams(this.window.location.search),
      ...this._tabTracking.getTabProperties(),
    }
  }

  private getPageProperties(): Record<string, unknown> {
    return {
      ...this.getDefaultProperties(),
      url: scrubSensitiveParams(this.window.location.href),
      referrer: scrubSensitiveParams(this._document.referrer),
    }
  }
}
